import React, { useState, useEffect, useMemo } from 'react';
import { Line } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  LineElement,
  PointElement,
  LinearScale,
  TimeScale,
  Tooltip,
  Legend,
} from 'chart.js';
import 'chartjs-adapter-date-fns';
import { attributionAPI } from '../../../services/api';

ChartJS.register(LineElement, PointElement, LinearScale, TimeScale, Tooltip, Legend);

const niceCeil = (v) => {
  if (v <= 0) return 1;
  const mag = Math.pow(10, Math.floor(Math.log10(v)));
  const norm = v / mag;
  let nice;
  if (norm <= 1) nice = 1;
  else if (norm <= 2) nice = 2;
  else if (norm <= 5) nice = 5;
  else nice = 10;
  return nice * mag;
};

const computeBounds = (values) => {
  if (!values.length) return { min: undefined, max: undefined, step: 1 };
  const rawMin = Math.min(0, ...values);
  const rawMax = Math.max(0, ...values);
  const range = Math.max(rawMax - rawMin, 0.0001);
  const step = niceCeil(range / 6);
  const min = Math.floor(rawMin / step) * step;
  const max = Math.ceil(rawMax / step) * step;
  return { min, max, step };
};

const CumulativeBrinsonChart = ({ portfolioCode, reportType, periodEnd, rangeStart, rangeEnd }) => {
  const [brinsonSeries, setBrinsonSeries] = useState([]);
  const [returnsSeries, setReturnsSeries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [range, setRange] = useState({ start: 0, end: 0 });

  useEffect(() => {
    if (!portfolioCode) {
      setBrinsonSeries([]);
      setReturnsSeries([]);
      return;
    }

    const handle = (payload) => {
      const b = payload?.brinson_series || [];
      const r = payload?.returns_series || [];
      setBrinsonSeries(b);
      setReturnsSeries(r);
      setRange({ start: 0, end: Math.max(0, b.length - 1) });
      setLoading(false);
    };

    if (reportType === 'RANGE') {
      if (!rangeStart || !rangeEnd) {
        setBrinsonSeries([]);
        setReturnsSeries([]);
        return;
      }
      setLoading(true);
      attributionAPI.getRangeBrinsonTimeSeries(portfolioCode, { start_date: rangeStart, end_date: rangeEnd })
        .then(handle)
        .catch((err) => { console.error('Failed to load range Brinson time series', err); setLoading(false); });
      return;
    }

    setLoading(true);
    const params = { report_type: reportType };
    if (reportType !== 'SI' && periodEnd) params.period_end = periodEnd;
    attributionAPI.getBrinsonTimeSeries(portfolioCode, params)
      .then(handle)
      .catch((err) => { console.error('Failed to load Brinson time series', err); setLoading(false); });
  }, [portfolioCode, reportType, periodEnd, rangeStart, rangeEnd]);

  const anchorDate = brinsonSeries[range.start]?.date;
  const endDate = brinsonSeries[range.end]?.date;

  const brinsonRebased = useMemo(() => {
    if (!brinsonSeries.length) return [];
    const lo = Math.min(range.start, range.end);
    const hi = Math.max(range.start, range.end);
    const anchor = brinsonSeries[lo] || {};
    return brinsonSeries.slice(lo, hi + 1).map(d => ({
      date: d.date,
      cum_allocation_effect: (d.cum_allocation_effect ?? 0) - (anchor.cum_allocation_effect ?? 0),
      cum_selection_effect: (d.cum_selection_effect ?? 0) - (anchor.cum_selection_effect ?? 0),
      cum_total_effect: (d.cum_total_effect ?? 0) - (anchor.cum_total_effect ?? 0),
    }));
  }, [brinsonSeries, range]);

  const returnsRebased = useMemo(() => {
    if (!returnsSeries.length || !anchorDate || !endDate) return [];
    const inWindow = returnsSeries.filter(d => d.date >= anchorDate && d.date <= endDate);
    if (!inWindow.length) return [];
    const anchorPort = inWindow[0].portfolio_cum_return_pct ?? 0;
    const anchorBm = inWindow[0].benchmark_cum_return_pct ?? 0;
    return inWindow.map(d => ({
      date: d.date,
      portfolio_cum_return_pct: (d.portfolio_cum_return_pct ?? 0) - anchorPort,
      benchmark_cum_return_pct: (d.benchmark_cum_return_pct ?? 0) - anchorBm,
    }));
  }, [returnsSeries, anchorDate, endDate]);

  const returnsBounds = useMemo(
    () => computeBounds(returnsRebased.flatMap(d => [d.portfolio_cum_return_pct, d.benchmark_cum_return_pct])),
    [returnsRebased],
  );
  const brinsonBounds = useMemo(
    () => computeBounds(brinsonRebased.flatMap(d => [d.cum_allocation_effect, d.cum_selection_effect, d.cum_total_effect])),
    [brinsonRebased],
  );

  if (loading) return <div className="chart-message">Loading…</div>;
  if (!brinsonSeries.length) return <div className="chart-message">No data available.</div>;

  const xUnit = reportType === 'SI' ? 'year' : 'month';

  const tooltipTitleCallback = (items) => {
    if (!items.length) return '';
    const d = new Date(items[0].parsed.x);
    return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
  };

  const sharedXScale = {
    type: 'time',
    time: { unit: xUnit },
    min: anchorDate,
    max: endDate,
    ticks: { font: { size: 10 } },
  };

  const returnsChartData = {
    datasets: [
      {
        label: 'Portfolio',
        data: returnsRebased.map(d => ({ x: d.date, y: d.portfolio_cum_return_pct })),
        borderColor: '#c8102e',
        backgroundColor: 'transparent',
        borderWidth: 2.5,
        pointRadius: 0,
        tension: 0.1,
      },
      {
        label: 'Benchmark',
        data: returnsRebased.map(d => ({ x: d.date, y: d.benchmark_cum_return_pct })),
        borderColor: '#599BE3',
        backgroundColor: 'transparent',
        borderWidth: 2,
        pointRadius: 0,
        tension: 0.1,
      },
    ],
  };

  const returnsOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom', labels: { boxWidth: 12, font: { size: 11 } } },
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          title: tooltipTitleCallback,
          label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y?.toFixed(2)}%`,
        },
      },
    },
    interaction: { mode: 'index', intersect: false },
    scales: {
      x: sharedXScale,
      y: {
        title: { display: true, text: 'Cumulative Return (%)', font: { size: 10 } },
        ticks: { font: { size: 10 }, stepSize: returnsBounds.step, callback: (v) => `${Math.round(v)}%` },
        grid: { color: '#eee' },
        min: returnsBounds.min,
        max: returnsBounds.max,
      },
    },
  };

  const brinsonChartData = {
    datasets: [
      {
        label: 'Allocation',
        data: brinsonRebased.map(d => ({ x: d.date, y: d.cum_allocation_effect })),
        borderColor: '#91CF9B',
        backgroundColor: 'transparent',
        borderWidth: 1.8,
        borderDash: [4, 4],
        pointRadius: 0,
        tension: 0.1,
      },
      {
        label: 'Selection',
        data: brinsonRebased.map(d => ({ x: d.date, y: d.cum_selection_effect })),
        borderColor: '#f0a500',
        backgroundColor: 'transparent',
        borderWidth: 1.8,
        borderDash: [4, 4],
        pointRadius: 0,
        tension: 0.1,
      },
      {
        label: 'Total',
        data: brinsonRebased.map(d => ({ x: d.date, y: d.cum_total_effect })),
        borderColor: '#072040',
        backgroundColor: 'transparent',
        borderWidth: 2.5,
        pointRadius: 0,
        tension: 0.1,
      },
    ],
  };

  const brinsonOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'bottom', labels: { boxWidth: 12, font: { size: 11 } } },
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          title: tooltipTitleCallback,
          label: (ctx) => `${ctx.dataset.label}: ${ctx.parsed.y?.toFixed(2)}%`,
        },
      },
    },
    interaction: { mode: 'index', intersect: false },
    scales: {
      x: sharedXScale,
      y: {
        title: { display: true, text: 'Brinson (%)', font: { size: 10 } },
        ticks: { font: { size: 10 }, stepSize: brinsonBounds.step, callback: (v) => `${v.toFixed(1)}%` },
        grid: { color: '#eee' },
        min: brinsonBounds.min,
        max: brinsonBounds.max,
      },
    },
  };

  return (
    <div className="chart-wrapper">
      <div className="chart-container chart-container-half">
        <Line data={returnsChartData} options={returnsOptions} />
      </div>
      <div className="chart-container chart-container-half">
        <Line data={brinsonChartData} options={brinsonOptions} />
      </div>
      <div className="chart-range-controls">
        <span className="chart-range-label">{anchorDate}</span>
        <input
          type="range"
          min={0}
          max={brinsonSeries.length - 1}
          value={range.start}
          onChange={(e) => {
            const v = Number(e.target.value);
            setRange((r) => ({ start: v, end: Math.max(v, r.end) }));
          }}
        />
        <input
          type="range"
          min={0}
          max={brinsonSeries.length - 1}
          value={range.end}
          onChange={(e) => {
            const v = Number(e.target.value);
            setRange((r) => ({ start: Math.min(r.start, v), end: v }));
          }}
        />
        <span className="chart-range-label">{endDate}</span>
        {(range.start !== 0 || range.end !== brinsonSeries.length - 1) && (
          <button
            className="chart-range-reset"
            onClick={() => setRange({ start: 0, end: brinsonSeries.length - 1 })}
          >
            Reset
          </button>
        )}
      </div>
    </div>
  );
};

export default CumulativeBrinsonChart;
